import * as Sentry from '@sentry/react';

// GQL
import { client } from '../client';
import { GET_ORDER_BY_ID } from '../queries/orders/orders';
import { FETCH_APP_USERS, GET_USER_BY_ID } from '../queries/users';

// Types
import { AuthContextType } from '../../hooks/useAuth';

// Utils
import { buildAppUserOptions } from '../../utils/appUserOptions';
import { OperatorOption } from '../../utils/registeredByOperators';

type Person = {
  id: string;
  name?: string | null;
  surName?: string | null;
  userType?: string | null;
  isActive?: boolean | null;
};

export type DuplicateAuthor = {
  /** Autor con el que arranca el formulario (el de la original). */
  userId: string;
  /** Su nombre, para el campo `operator` que pinta el resumen. */
  operator: string;
  appUserOptions: OperatorOption[];
};

/**
 * «Operario» del formulario de DUPLICAR OT o comunicación: con qué autor
 * arranca (el de la ORIGINAL) y entre quiénes se puede elegir.
 *
 * Cada consulta falla por separado y en silencio (Sentry): sin cobertura el
 * formulario tiene que seguir abriéndose. En el peor caso las opciones son el
 * logueado y el autor queda en el logueado — lo mismo que guardaría el
 * backend sin `userId`.
 *
 * Spec: docs/superpowers/specs/2026-09-24-duplicar-operario-editable-design.md § 3
 */
export const selectDuplicateAuthor = async (
  orderId: string,
  userData: AuthContextType | null
): Promise<DuplicateAuthor> => {
  const loggedUser = userData?.user?.user;
  const me: Person | null = loggedUser?.id
    ? { id: loggedUser.id, name: loggedUser.name, surName: loggedUser.surName }
    : null;

  let appUsers: Person[] = [];
  try {
    const { data } = await client.query({ query: FETCH_APP_USERS });
    appUsers = data?.appUsers ?? [];
  } catch (err) {
    Sentry.captureException(err);
  }

  let original: Person | null = null;
  try {
    const {
      data: { order },
    } = await client.query({ query: GET_ORDER_BY_ID, variables: { id: orderId } });
    const authorId: string | undefined = order?.userId;
    if (authorId) {
      original = appUsers.find((user) => user.id === authorId) ?? null;
      if (!original) {
        // Fuera de la lista (dado de baja, u otro tenant activo): su nombre
        // hace falta igual para que el desplegable no salga vacío.
        const { data } = await client.query({
          query: GET_USER_BY_ID,
          variables: { input: { id: authorId } },
        });
        original = data?.user ?? { id: authorId };
      }
    }
  } catch (err) {
    Sentry.captureException(err);
  }

  const appUserOptions = buildAppUserOptions(appUsers, me, original);
  const userId = original?.id ?? me?.id ?? '';
  return {
    userId,
    operator: appUserOptions.find((option) => option.id === userId)?.name ?? '',
    appUserOptions,
  };
};
